/**
 * Unread conversations, for the inbox dots and the header's inbox count.
 *
 * Sharetribe has no read receipts or unread counts, so we keep two records on
 * the user's own profile, both loaded with the current user on every page:
 *
 * - profile.privateData.inboxLastMessageAt: { <txId>: { at: <ms>, role } }
 *   When the other party last messaged in each conversation, and which side
 *   the user is on there ('provider' or 'customer'), so each inbox tab can
 *   count its own. Written by our server when a message is sent
 *   (server/api/message-sent.js).
 *
 * - profile.privateData.inboxReadAt: { since: <ms>, transactions: { <txId>: <ms> } }
 *   The newest message time the user has seen in each conversation. Written by
 *   the user's own client when they open a conversation. `since` only exists
 *   once old entries have been pruned, and covers them.
 *
 * A conversation is unread when its last message from the other party is newer
 * than what the user has seen. Both times come from the API, so device clocks
 * don't matter. Because this needs no per-conversation requests, counting unread
 * conversations is free on every page.
 */

export const LAST_MESSAGE_KEY = 'inboxLastMessageAt';
export const READ_STATE_KEY = 'inboxReadAt';

// Extended data has a size cap, so only the most recently read conversations
// are kept. ~200 entries is roughly 12KB.
export const MAX_TRACKED_TRANSACTIONS = 200;

const isObject = value => !!value && typeof value === 'object';

/**
 * @param {Object} currentUser - current user entity
 * @returns {Object} txId -> { at, role }: when the other party last messaged,
 *   in milliseconds, and the user's side in that conversation
 */
export const getLastMessageTimes = currentUser => {
  const stored = currentUser?.attributes?.profile?.privateData?.[LAST_MESSAGE_KEY];
  if (!isObject(stored)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(stored).filter(([, entry]) => typeof entry?.at === 'number')
  );
};

/**
 * @param {Object} currentUser - current user entity
 * @returns {{since: number, transactions: Object}} what the user has seen
 */
export const getReadState = currentUser => {
  const stored = currentUser?.attributes?.profile?.privateData?.[READ_STATE_KEY];
  return {
    since: typeof stored?.since === 'number' ? stored.since : 0,
    transactions: isObject(stored?.transactions) ? stored.transactions : {},
  };
};

/**
 * @param {Object} params
 * @param {string} params.txId - transaction UUID
 * @param {Object} params.lastMessageTimes - from getLastMessageTimes
 * @param {{since: number, transactions: Object}} params.readState - from getReadState
 * @returns {boolean} true when the other party has messaged since the user last looked
 */
export const isConversationUnread = ({ txId, lastMessageTimes, readState }) => {
  const lastMessageAt = lastMessageTimes?.[txId]?.at;
  if (typeof lastMessageAt !== 'number') {
    return false;
  }
  const readAt = readState?.transactions?.[txId] ?? readState?.since ?? 0;
  return lastMessageAt > readAt;
};

/**
 * @param {Object} currentUser - current user entity
 * @param {'provider'|'customer'} [role] - only count conversations where the
 *   user is on this side; all of them when left out
 * @returns {number} how many conversations have an unread message
 */
export const countUnreadConversations = (currentUser, role) => {
  const lastMessageTimes = getLastMessageTimes(currentUser);
  const readState = getReadState(currentUser);
  return Object.keys(lastMessageTimes).filter(
    txId =>
      (!role || lastMessageTimes[txId].role === role) &&
      isConversationUnread({ txId, lastMessageTimes, readState })
  ).length;
};

/**
 * The state to save when opening a conversation, or null when nothing changes.
 * Times only move forward, so paging back to older messages can't undo a read.
 *
 * @param {{since: number, transactions: Object}} readState - from getReadState
 * @param {string} txId - transaction UUID
 * @param {number} readAt - creation time of the newest seen message, in milliseconds
 * @returns {{since: number, transactions: Object}|null} new state to save, or null
 */
export const withTransactionRead = (readState, txId, readAt) => {
  if (!txId || !readAt) {
    return null;
  }
  const since = readState?.since || 0;
  const transactions = readState?.transactions || {};
  if (transactions[txId] >= readAt || since >= readAt) {
    return null;
  }

  const entries = Object.entries({ ...transactions, [txId]: readAt }).sort(
    ([, a], [, b]) => b - a
  );
  const kept = entries.slice(0, MAX_TRACKED_TRANSACTIONS);
  const dropped = entries.slice(MAX_TRACKED_TRANSACTIONS);

  // A dropped conversation falls back to `since`, so move it up to cover the
  // dropped entries. Otherwise their last message would show as unread again.
  const nextSince = dropped.reduce((max, [, at]) => Math.max(max, at), since);
  return { since: nextSince, transactions: Object.fromEntries(kept) };
};
